<template>
  <base-section
    v-if="visible"
    id="about-team-vision"
    space="72"
  >
    <v-container style="max-width: 1160px">
      <base-section-heading
        v-if="sectionTitle || sectionText"
        :title="sectionTitle"
        :subtitle="sectionText"
        :size="cmsTitleFontSize || undefined"
        :mobile-size="cmsTitleMobileFontSize || 'text-h5'"
        :subtitle-size="cmsSubtitleFontSize || undefined"
        :subtitle-mobile-size="cmsSubtitleMobileFontSize || undefined"
        :is-uppercase="false"
        :space="6"
      />

      <v-row>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            outlined
            class="pa-6 h-100"
          >
            <div class="section-title">
              {{ teamData.title }}
            </div>
            <div
              v-if="teamData.subtitle"
              class="section-subtitle"
            >
              {{ teamData.subtitle }}
            </div>
            <base-body class="mt-3">
              {{ teamData.content }}
            </base-body>
            <v-img
              v-if="teamData.image"
              :src="teamData.image"
              :alt="teamData.title || 'team'"
              class="mt-4"
            />
          </v-card>
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-card
            outlined
            class="pa-6 h-100"
          >
            <div class="section-title">
              {{ visionData.title }}
            </div>
            <div
              v-if="visionData.subtitle"
              class="section-subtitle"
            >
              {{ visionData.subtitle }}
            </div>
            <base-body class="mt-3">
              {{ visionData.content }}
            </base-body>
            <v-img
              v-if="visionData.image"
              :src="visionData.image"
              :alt="visionData.title || 'vision'"
              class="mt-4"
            />
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  export default {
    name: 'SectionAboutTeamVision',

    data: () => ({
      moduleEnabled: true,
      cmsTitle: '',
      cmsText: '',
      cmsTitleFontSize: '',
      cmsTitleMobileFontSize: '',
      cmsSubtitleFontSize: '',
      cmsSubtitleMobileFontSize: '',
      cmsTeam: null,
      cmsVision: null,
    }),

    computed: {
      visible() {
        return this.moduleEnabled
      },
      sectionTitle() {
        return this.cmsTitle || undefined
      },
      sectionText() {
        return this.cmsText || undefined
      },
      teamData() {
        const cms = this.cmsTeam
        return {
          title: cms?.title || this.$t('about.teamTitle'),
          subtitle: cms?.subtitle || this.$t('about.teamSubtitle'),
          content: cms?.content || this.$t('about.teamContent'),
          image: cms?.image || '',
        }
      },
      visionData() {
        const cms = this.cmsVision
        return {
          title: cms?.title || this.$t('about.visionTitle'),
          subtitle: cms?.subtitle || this.$t('about.visionSubtitle'),
          content: cms?.content || this.$t('about.visionContent'),
          image: cms?.image || '',
        }
      },
    },

    watch: {
      '$i18n.locale'() {
        this.loadContent()
      },
    },

    async mounted() {
      await this.loadContent()
      window.addEventListener('resize', this.equalizeHeights)
    },

    beforeDestroy() {
      window.removeEventListener('resize', this.equalizeHeights)
    },

    methods: {
      equalizeHeights() {
        this.$nextTick(() => {
          const titles = this.$el.querySelectorAll('.section-title')
          const descs = this.$el.querySelectorAll('.section-subtitle')
          titles.forEach(el => { el.style.minHeight = '' })
          descs.forEach(el => { el.style.minHeight = '' })
          let maxT = 0
          titles.forEach(el => { maxT = Math.max(maxT, el.offsetHeight) })
          if (maxT > 0) titles.forEach(el => { el.style.minHeight = maxT + 'px' })
          let maxD = 0
          descs.forEach(el => { maxD = Math.max(maxD, el.offsetHeight) })
          if (maxD > 0) descs.forEach(el => { el.style.minHeight = maxD + 'px' })
        })
      },
      async loadContent() {
        try {
          const modules = await this.$axios({
            method: 'get',
            url: '/public/modules/page/about',
            params: { lang: this.$i18n.locale },
          })
          const module = (modules || []).find(m => m?.moduleKey === 'about_team_vision')

          if (!module) {
            // No module configured — hide section
            this.moduleEnabled = false
            this.cmsTeam = null
            this.cmsVision = null
            return
          }

          if (module.enabled === false) {
            this.moduleEnabled = false
            return
          }

          this.moduleEnabled = true
          this.cmsTitle = module.title || ''
          this.cmsText = module.subtitle || ''
          this.cmsTitleFontSize = module.titleFontSize || ''
          this.cmsTitleMobileFontSize = module.titleMobileFontSize || ''
          this.cmsSubtitleFontSize = module.subtitleFontSize || ''
          this.cmsSubtitleMobileFontSize = module.subtitleMobileFontSize || ''

          if (module.content) {
            try {
              const parsed = JSON.parse(module.content)
              this.cmsTeam = parsed.team || null
              this.cmsVision = parsed.vision || null
            } catch {
              this.cmsTeam = null
              this.cmsVision = null
            }
          }
        } catch {
          // API unavailable — hide section
          this.moduleEnabled = false
          this.cmsTeam = null
          this.cmsVision = null
        }
        this.equalizeHeights()
      },
    },
  }
</script>

<style lang="sass" scoped>
.section-title
  font-size: 20px
  font-weight: 600
  color: rgba(0, 0, 0, 0.9)

.section-subtitle
  margin-top: 6px
  color: rgba(0, 0, 0, 0.7)
  font-size: 14px

.h-100
  height: 100%
  display: flex
  flex-direction: column
  border-radius: 0 !important
</style>
